import { checkForInvalid, Quadrant } from '../calculators/EnergyFlow'
import { Provider } from '../calculators/providers/common/Provider'
import {
  HybridHeatPumpExtraOutputField,
  isHybridHeatPumpProvider,
} from '../calculators/providers/HybridHeatPumpProvider'
import { getValuesForMonth } from '../components/common/util/date-util'
import {
  BoreholeFieldConfirmationData,
  ProviderType,
} from '../generated/graphql'
import {
  groundHeatGivingProviderTypes,
  groundHeatUsingProviderTypes,
} from '../provider/provider-properties'
import { reduceToSum, reduceToSumOfItems } from '../util/array-utility'
import { BoreholeFieldContainerFragment } from './borehole-field-confirmation-validator'

const getValuesFromProviders = (
  providerTypes: ProviderType[],
  quadrant: Quadrant,
  providers: Provider[],
) =>
  providers
    .filter((provider) => providerTypes.includes(provider.type))
    .flatMap((provider) => provider.flows)
    .filter(checkForInvalid)
    .filter((flow) => flow.quadrant === quadrant)
    .map((flow) => flow.hourSeries)
    .reduce(reduceToSumOfItems, [])

const getValuesFromHybridHeatPumpProviders = (
  providers: Provider[],
  hybridHeatPumpField: HybridHeatPumpExtraOutputField,
) =>
  providers
    .filter(isHybridHeatPumpProvider)
    .map((provider) => provider.extraOutputValues?.[hybridHeatPumpField] ?? [])
    .reduce(reduceToSumOfItems, [])

export function createHarvestedDataForSystemContainer(
  providers: Provider[],
): number[] {
  return reduceToSumOfItems(
    getValuesFromProviders(
      groundHeatUsingProviderTypes,
      Quadrant.FromEnvironment,
      providers,
    ),
    getValuesFromHybridHeatPumpProviders(
      providers,
      HybridHeatPumpExtraOutputField.GroundSourceHeating,
    ),
  )
}
export function createWasteInputDataForSystemContainer(
  providers: Provider[],
): number[] {
  return reduceToSumOfItems(
    getValuesFromProviders(
      groundHeatGivingProviderTypes,
      Quadrant.Waste,
      providers,
    ),
    getValuesFromHybridHeatPumpProviders(
      providers,
      HybridHeatPumpExtraOutputField.GroundSourceCooling,
    ),
  )
}

export function createConfirmationData(
  container: BoreholeFieldContainerFragment,
  harvested: number[],
  wasteInput: number[],
): BoreholeFieldConfirmationData {
  const getUsingValues = (monthIndex?: number) =>
    getValuesForMonth(harvested, monthIndex)
  const getGivingValues = (monthIndex?: number) =>
    getValuesForMonth(wasteInput, monthIndex)

  const monthIndices = Array.from(Array(12).keys())

  return {
    holeDepth: container.holeDepth,
    holeCount: container.holeCount,

    holeUsageForHeatingMonthlyTotal: monthIndices.map((monthIndex) =>
      getUsingValues(monthIndex).reduce(reduceToSum, 0),
    ),
    holeUsageForHeatingMonthlyMax: monthIndices.map((monthIndex) =>
      Math.max(...getUsingValues(monthIndex), 0),
    ),
    holeUsageForCoolingMonthlyTotal: monthIndices.map((monthIndex) =>
      getGivingValues(monthIndex).reduce(reduceToSum, 0),
    ),
    holeUsageForCoolingMonthlyMax: monthIndices.map((monthIndex) =>
      Math.max(...getGivingValues(monthIndex), 0),
    ),
  }
}
